import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    UserPlus,
    CheckCircle,
    XCircle,
    Loader2,
    ArrowRight,
    Users,
    Shield,
} from 'lucide-react';

export function InvitePage() {
    const { code } = useParams<{ code: string }>();
    const { user, profile, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [inviteData, setInviteData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        const fetchInvite = async () => {
            if (!code) return;

            try {
                const { data, error } = await supabase
                    .from('student_pairings')
                    .select(`
                        *,
                        personal:personal_id (
                            full_name,
                            avatar_url
                        ),
                        tenant:tenant_id (
                            name,
                            logo_url
                        )
                    `)
                    .eq('invite_code', code)
                    .maybeSingle();

                if (error) throw error;
                if (!data) {
                    setError('Convite não encontrado ou já expirado.');
                } else if (data.status === 'active') {
                    setError('Este convite já foi aceito.');
                } else {
                    setInviteData(data);
                }
            } catch (err) {
                console.error('Error fetching invite:', err);
                setError('Erro ao carregar convite.');
            } finally {
                setLoading(false);
            }
        };

        fetchInvite();
    }, [code]);

    const handleAccept = async () => {
        if (!user || !code) return;
        setAccepting(true);

        try {
            const { error } = await supabase
                .from('student_pairings')
                .update({
                    student_id: user.id,
                    status: 'active',
                    paired_at: new Date().toISOString(),
                })
                .eq('invite_code', code);

            if (error) throw error;

            // Redirect to student dashboard
            navigate('/atleta');
        } catch (err) {
            console.error('Error accepting invite:', err);
            alert('Erro ao aceitar convite.');
        } finally {
            setAccepting(false);
        }
    };

    if (loading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
                <div className="card max-w-md w-full text-center">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ops!</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                    <Link to="/" className="btn-primary w-full">
                        Voltar para o Início
                    </Link>
                </div>
            </div>
        );
    }

    const { personal, tenant } = inviteData;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
            <div className="card max-w-md w-full p-8 text-center space-y-6">
                {/* Visual Header */}
                <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 mx-auto flex items-center justify-center shadow-lg">
                        <Users className="w-12 h-12 text-white" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white dark:bg-gray-900 border-4 border-gray-50 dark:border-gray-950 flex items-center justify-center shadow">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                </div>

                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Você foi convidado!</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        O Personal Trainer <span className="font-bold text-primary-600">{personal?.full_name}</span> quer te acompanhar na jornada fitness.
                    </p>
                </div>

                <div className="p-4 rounded-xl bg-gray-100 dark:bg-gray-800/50 flex items-center gap-4 text-left">
                    <div className="w-12 h-12 rounded-lg bg-white dark:bg-gray-900 flex items-center justify-center border font-bold text-primary-600">
                        {tenant?.name?.charAt(0) || 'G'}
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Organização</p>
                        <p className="font-bold text-gray-900 dark:text-white">{tenant?.name}</p>
                    </div>
                </div>

                <div className="space-y-3">
                    {user ? (
                        <>
                            <button
                                onClick={handleAccept}
                                disabled={accepting}
                                className="btn-primary w-full py-4 text-lg"
                            >
                                {accepting ? (
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                ) : (
                                    'Aceitar e Começar'
                                )}
                            </button>
                            <p className="text-xs text-gray-500">
                                Você está logado como <span className="font-medium">{user.email}</span>.
                            </p>
                        </>
                    ) : (
                        <>
                            <Link
                                to={`/signup?invite=${code}&role=student`}
                                className="btn-primary w-full py-4 text-lg"
                            >
                                Criar Conta e Aceitar
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Link>
                            <p className="text-sm text-gray-500">
                                Já tem uma conta?{' '}
                                <Link to={`/login?redirect=/convite/${code}`} className="text-primary-600 font-bold">
                                    Fazer Login
                                </Link>
                            </p>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-center gap-2 pt-4 border-t text-xs text-gray-400">
                    <Shield className="w-3 h-3" />
                    Ambiente Seguro GoFit 💜
                </div>
            </div>
        </div>
    );
}
